/**
 * A PDF opened in the browser, so its pages can be drawn beside what was read off them.
 *
 * pdf.js is over a megabyte with its worker, so it is imported dynamically, as the engraver
 * is: only the Read board draws a PDF, and no other board is made slower by it. The module
 * promise is the cache.
 *
 * The file is never sent anywhere to be drawn. What the server is sent is the scan to read,
 * and that is the reader's business (`scan-read.ts`).
 */

import { useEffect, useState } from "react";
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from "pdfjs-dist";
import { said } from "./job-poll";

type Pdfjs = typeof import("pdfjs-dist");

let loading: Promise<Pdfjs> | null = null;

async function load(): Promise<Pdfjs> {
  const [pdfjs, worker] = await Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
}

/** The most device pixels a page is drawn at for each CSS pixel: sharp, and not a 4K canvas. */
const SHARPEST = 2;

/**
 * Draw page `number` of `pdf` on `canvas`, `width` CSS pixels wide.
 *
 * Resolves with a way to stop the drawing, and `done`, which settles when the page is drawn
 * or the drawing was stopped: pdf.js refuses to draw twice on one canvas at once.
 */
export async function drawn(
  pdf: PDFDocumentProxy,
  number: number,
  canvas: HTMLCanvasElement,
  width: number,
): Promise<{ stop(): void; done: Promise<void> }> {
  const page = await pdf.getPage(number);
  const flat = page.getViewport({ scale: 1 });
  const sharp = Math.min(window.devicePixelRatio || 1, SHARPEST);
  const viewport = page.getViewport({ scale: (width * sharp) / flat.width });
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const task = page.render({ canvas, viewport });
  return {
    stop: () => task.cancel(),
    // A drawing stopped rejects, and there is nothing to say about one that was stopped.
    done: task.promise.then(
      () => undefined,
      () => undefined,
    ),
  };
}

export interface OpenedPdf {
  /** The document, once it is open; `null` before, and where it could not be opened. */
  readonly pdf: PDFDocumentProxy | null;
  /** Why `file` could not be opened as a PDF. */
  readonly error: string | null;
}

/** `file` opened as a PDF, and closed when another takes its place. */
export function usePdf(file: File | null): OpenedPdf {
  const [opened, setOpened] = useState<OpenedPdf>({ pdf: null, error: null });
  useEffect(() => {
    setOpened({ pdf: null, error: null });
    if (file === null) return undefined;
    let live = true;
    // The task, not the document: pdf.js starts a worker for each, and only the task's
    // destroy stops it, whether the file opened or not.
    let task: PDFDocumentLoadingTask | null = null;
    const opening = async (): Promise<void> => {
      loading ??= load();
      const pdfjs = await loading;
      const data = await file.arrayBuffer();
      if (!live) return;
      // Where pdf.js fetches the decoders of JPEG 2000 and JBIG2 images, which most scans
      // are; without them such a page is drawn white (vite.config.ts serves the folder).
      const wasmUrl = new URL(`${import.meta.env.BASE_URL}pdfjs/wasm/`, location.href).href;
      task = pdfjs.getDocument({ data, wasmUrl });
      const pdf = await task.promise;
      if (live) setOpened({ pdf, error: null });
    };
    opening().catch((cause: unknown) => {
      void task?.destroy();
      if (live) setOpened({ pdf: null, error: said(cause) });
    });
    return () => {
      live = false;
      void task?.destroy();
    };
  }, [file]);
  return opened;
}
